const usageMethods = Array.from(
  document.querySelectorAll<HTMLButtonElement>('[data-usage-method]'),
)
const usageTabs = Array.from(
  document.querySelectorAll<HTMLButtonElement>('[data-usage-tab]'),
)
const usageTabList = document.querySelector<HTMLElement>('.code-tablist')

function selectUsage(tab: HTMLButtonElement, focus = false): void {
  for (const candidate of usageTabs) {
    const selected = candidate === tab
    candidate.setAttribute('aria-selected', String(selected))
    candidate.tabIndex = selected ? 0 : -1
    const panelId = candidate.dataset.usageTab
    const panel = panelId ? document.querySelector<HTMLElement>(`#${panelId}`) : null
    if (panel) panel.hidden = !selected
  }
  if (focus) tab.focus()
}

function selectMethod(button: HTMLButtonElement): void {
  const method = button.dataset.usageMethod
  if (!method) return

  for (const candidate of usageMethods) {
    candidate.setAttribute('aria-pressed', String(candidate === button))
  }
  for (const tab of usageTabs) tab.hidden = tab.dataset.method !== method

  usageTabList?.setAttribute('aria-label', `${button.textContent?.trim()} variation`)
  const firstTab = usageTabs.find((tab) => !tab.hidden)
  if (firstTab) selectUsage(firstTab)
}

for (const button of usageMethods) {
  button.addEventListener('click', () => selectMethod(button))
}

for (const tab of usageTabs) {
  tab.addEventListener('click', () => selectUsage(tab))
  tab.addEventListener('keydown', (event) => {
    const visibleTabs = usageTabs.filter((candidate) => !candidate.hidden)
    const index = visibleTabs.indexOf(tab)
    let nextIndex: number | undefined
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % visibleTabs.length
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + visibleTabs.length) % visibleTabs.length
    if (event.key === 'Home') nextIndex = 0
    if (event.key === 'End') nextIndex = visibleTabs.length - 1
    if (nextIndex === undefined) return
    event.preventDefault()
    selectUsage(visibleTabs[nextIndex]!, true)
  })
}

for (const button of document.querySelectorAll<HTMLButtonElement>('[data-copy-active]')) {
  button.addEventListener('click', () => {
    const code = document.querySelector<HTMLElement>('[role="tabpanel"]:not([hidden]) code')
    if (code) void copyCode(button, code.textContent ?? '')
  })
}

async function copyCode(button: HTMLButtonElement, code: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(code)
    button.textContent = 'Copied'
    button.classList.add('copied')
    window.setTimeout(() => {
      button.textContent = 'Copy'
      button.classList.remove('copied')
    }, 1_800)
  } catch {
    button.textContent = 'Select to copy'
  }
}

interface LiveOptions {
  post: string
  title: string
  indentDepth: number
  colorScheme: 'auto' | 'light' | 'dark'
  accent?: string
  background?: string
}

interface LiveEmbedInstance {
  destroy(): void
}

interface LiveEmbedApi {
  embed(container: Element, options: LiveOptions): LiveEmbedInstance
}

function setupLivePlayground(): void {
  const controls = document.querySelector<HTMLFormElement>('[data-live-controls]')
  const comments = document.querySelector<HTMLElement>('.live-preview [data-calque]')
  const liveFrame = document.querySelector<HTMLElement>('.live-frame')
  const status = document.querySelector<HTMLElement>('[data-live-status]')
  const indentDepthOutput = document.querySelector<HTMLOutputElement>('[data-live-indent-depth-value]')
  if (!controls || !comments || !liveFrame || !status) return

  const embed = (window as Window & { Calque?: LiveEmbedApi }).Calque?.embed
  if (!embed) {
    status.textContent = 'The live playground is unavailable right now.'
    return
  }

  let instance: LiveEmbedInstance | undefined
  let updateTimer = 0
  let releaseHeightTimer = 0

  const releasePreviewHeight = (): void => {
    window.clearTimeout(releaseHeightTimer)
    liveFrame.style.removeProperty('min-height')
  }

  const holdPreviewHeight = (): void => {
    const height = liveFrame.getBoundingClientRect().height
    if (height <= 0) return
    window.clearTimeout(releaseHeightTimer)
    liveFrame.style.minHeight = `${Math.ceil(height)}px`
  }

  const inputFor = (name: string): HTMLInputElement | null =>
    controls.querySelector<HTMLInputElement>(`[data-live-option="${name}"]`)

  const updateIndentDepthOutput = (): void => {
    const indentDepth = inputFor('indentDepth')
    if (indentDepthOutput && indentDepth) indentDepthOutput.value = indentDepth.value
  }

  const readOptions = (): LiveOptions => {
    const title = inputFor('title')
    const indentDepth = inputFor('indentDepth')
    const indentDepthValue = Number(indentDepth?.value)
    const normalizedIndentDepth = Number.isFinite(indentDepthValue)
      ? Math.min(5, Math.max(1, Math.floor(indentDepthValue)))
      : 1
    if (indentDepth) indentDepth.value = String(normalizedIndentDepth)
    const scheme = controls.querySelector<HTMLInputElement>('[data-live-option="colorScheme"]:checked')
    const options: LiveOptions = {
      post: comments.dataset.post ?? '',
      title: title?.value.trim() || 'Conversation',
      indentDepth: normalizedIndentDepth,
      colorScheme: scheme?.value === 'light' || scheme?.value === 'dark' ? scheme.value : 'auto',
    }

    for (const name of ['accent', 'background'] as const) {
      const enabled = controls.querySelector<HTMLInputElement>(`[data-live-color-enabled="${name}"]`)
      const color = inputFor(name)
      if (enabled?.checked && color?.value) options[name] = color.value
    }
    return options
  }

  const render = (): void => {
    const options = readOptions()
    updateIndentDepthOutput()
    holdPreviewHeight()
    instance?.destroy()
    instance = embed(comments, options)

    const iframe = comments.querySelector<HTMLIFrameElement>('iframe')
    if (iframe) {
      iframe.addEventListener('load', () => {
        window.clearTimeout(releaseHeightTimer)
        releaseHeightTimer = window.setTimeout(releasePreviewHeight, 1_200)
      }, { once: true })
    }
    releaseHeightTimer = window.setTimeout(releasePreviewHeight, 5_000)
    status.textContent = `Showing “${options.title}” · indent replies up to ${options.indentDepth} levels · ${options.colorScheme} mode.`
  }

  const queueRender = (): void => {
    window.clearTimeout(updateTimer)
    updateTimer = window.setTimeout(render, 180)
  }

  for (const input of controls.querySelectorAll<HTMLInputElement>('[data-live-option]')) {
    input.addEventListener('input', () => {
      updateIndentDepthOutput()
      queueRender()
    })
    input.addEventListener('change', () => {
      updateIndentDepthOutput()
      queueRender()
    })
  }

  for (const toggle of controls.querySelectorAll<HTMLInputElement>('[data-live-color-enabled]')) {
    const name = toggle.dataset.liveColorEnabled
    const color = name ? inputFor(name) : null
    if (!color) continue
    const syncColor = (): void => {
      color.disabled = !toggle.checked
      queueRender()
    }
    toggle.addEventListener('change', syncColor)
    color.disabled = !toggle.checked
  }

  controls.addEventListener('submit', (event) => event.preventDefault())
  controls.addEventListener('reset', () => {
    window.setTimeout(() => {
      for (const toggle of controls.querySelectorAll<HTMLInputElement>('[data-live-color-enabled]')) {
        const name = toggle.dataset.liveColorEnabled
        const color = name ? inputFor(name) : null
        if (color) color.disabled = !toggle.checked
      }
      updateIndentDepthOutput()
      render()
    })
  })

  updateIndentDepthOutput()
  render()
}

setupLivePlayground()
